import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { funnels, opportunities } from "@/api/endpoints";
import { apiUrl, ApiError } from "@/api/client";
import type {
  Attachment,
  CapacityUnit,
  Funnel,
  FunnelKey,
  MetricDefinition,
  MetricValue,
  Note,
  OppStatus,
  OpportunityDetail as OppDetail,
  Stage,
  Transition,
} from "@/types/api";
import { canEdit, useCurrentUser } from "@/hooks/useAuth";
import { useToast } from "@/store/toastStore";
import {
  capacityLabel,
  formatCurrency,
  formatDateTime,
  formatNumber,
} from "@/lib/format";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Select,
  Spinner,
  TextInput,
  Textarea,
} from "@/components/ui";

/* --------------------------------------------------------------- helpers */

function errMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong";
}

const STATUS_COLORS: Record<OppStatus, "blue" | "green" | "red"> = {
  open: "blue",
  won: "green",
  lost: "red",
};

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const CAPACITY_UNITS: CapacityUnit[] = ["gpu", "node", "usd"];

/** Read a string-ish detail value for a controlled input. */
function detailStr(details: Record<string, unknown>, key: string): string {
  const v = details[key];
  if (v === null || v === undefined) return "";
  return String(v);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/* -------------------------------------------------------- field specs */

type FieldType = "text" | "number" | "date" | "select";

interface DetailFieldSpec {
  key: string;
  label: string;
  type: FieldType;
  options?: { value: string; label: string }[];
}

const DETAIL_FIELDS: Record<FunnelKey, DetailFieldSpec[]> = {
  customer: [
    { key: "company", label: "Company", type: "text" },
    { key: "contact_name", label: "Contact name", type: "text" },
    { key: "contact_email", label: "Contact email", type: "text" },
    { key: "expected_gpu_count", label: "Expected GPU count", type: "number" },
    { key: "gpu_type_pref", label: "GPU type preference", type: "text" },
    { key: "region_pref", label: "Region preference", type: "text" },
    {
      key: "contract_rate_per_gpu_hour",
      label: "Contract rate / GPU-hour",
      type: "number",
    },
    { key: "contract_start_date", label: "Contract start date", type: "date" },
    { key: "contract_length_months", label: "Contract length (months)", type: "number" },
    { key: "contract_prepaid_months", label: "Prepaid months", type: "number" },
  ],
  hardware: [
    { key: "gpu_type", label: "GPU type", type: "text" },
    { key: "gpus_per_node", label: "GPUs per node", type: "number" },
    { key: "quantity_gpus", label: "Quantity GPUs", type: "number" },
    { key: "unit_cost_per_node", label: "Unit cost / node", type: "number" },
    { key: "peripheral_cost_per_node", label: "Peripheral cost / node", type: "number" },
    { key: "vendor", label: "Vendor", type: "text" },
    {
      key: "networking",
      label: "Networking",
      type: "select",
      options: [
        { value: "none", label: "None" },
        { value: "roce", label: "RoCE" },
        { value: "infiniband", label: "InfiniBand" },
      ],
    },
    { key: "lead_time_days", label: "Lead time (days)", type: "number" },
    { key: "dc_location", label: "DC location", type: "text" },
    { key: "avg_power_draw_kw", label: "Avg power draw (kW)", type: "number" },
  ],
  financing: [
    {
      key: "instrument",
      label: "Instrument",
      type: "select",
      options: [
        { value: "loan", label: "Loan" },
        { value: "lease", label: "Lease" },
      ],
    },
    { key: "amount", label: "Amount", type: "number" },
    { key: "lender", label: "Lender", type: "text" },
    { key: "cash_down_pct", label: "Cash down %", type: "number" },
    { key: "monthly_rate_pct", label: "Monthly rate %", type: "number" },
    { key: "loan_term_months", label: "Loan term (months)", type: "number" },
    {
      key: "lease_type",
      label: "Lease type",
      type: "select",
      options: [
        { value: "operating", label: "Operating" },
        { value: "finance", label: "Finance" },
      ],
    },
    { key: "discount_rate", label: "Discount rate", type: "number" },
    { key: "lease_buyout_pct", label: "Lease buyout %", type: "number" },
    { key: "lease_deposit_pct", label: "Lease deposit %", type: "number" },
    { key: "monthly_lease_factor", label: "Monthly lease factor", type: "number" },
  ],
};

/* =========================================================== Header card */

function HeaderCard({
  opp,
  funnel,
  stage,
  editable,
}: {
  opp: OppDetail;
  funnel: Funnel | undefined;
  stage: Stage | undefined;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState(opp.title);

  useEffect(() => {
    setTitle(opp.title);
  }, [opp.title]);

  const stages = useMemo(
    () => (funnel?.stages ?? []).filter((s) => !s.archived_at).sort((a, b) => a.position - b.position),
    [funnel],
  );

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => opportunities.update(opp.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunity", opp.id] });
      toast("success", "Title saved");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const move = useMutation({
    mutationFn: (toStageId: string) => opportunities.move(opp.id, toStageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunity", opp.id] });
      toast("success", "Stage updated");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const submit = () => {
    const t = title.trim();
    if (!t) {
      toast("error", "Title is required");
      return;
    }
    if (t === opp.title) return;
    save.mutate({ title: t });
  };

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-[16rem] flex-1">
          {editable ? (
            <div className="flex items-center gap-2">
              <TextInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-base font-semibold"
              />
              <Button onClick={submit} disabled={save.isPending}>
                {save.isPending ? <Spinner /> : "Save"}
              </Button>
            </div>
          ) : (
            <h1 className="text-lg font-semibold text-slate-900">{opp.title}</h1>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <Badge color={STATUS_COLORS[opp.status]}>{opp.status}</Badge>
            <span className="text-slate-300">·</span>
            <span>{funnel?.name ?? "—"}</span>
            <span className="text-slate-300">·</span>
            <span>{stage?.name ?? "—"}</span>
            {opp.owner_user_id && (
              <>
                <span className="text-slate-300">·</span>
                <Badge color="violet">{opp.owner_user_id}</Badge>
              </>
            )}
          </div>
        </div>
        {editable && stages.length > 0 && (
          <div className="w-52">
            <Field label="Funnel stage">
              <Select
                value={opp.stage_id}
                disabled={move.isPending}
                onChange={(e) => {
                  const to = e.target.value;
                  if (to !== opp.stage_id) move.mutate(to);
                }}
                options={stages.map((s) => ({ value: s.id, label: s.name }))}
              />
            </Field>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ==================================================== Common fields card */

function CommonFieldsCard({ opp, editable }: { opp: OppDetail; editable: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [title, setTitle] = useState(opp.title);
  const [status, setStatus] = useState<OppStatus>(opp.status);
  const [valueAmount, setValueAmount] = useState(
    opp.value_amount == null ? "" : String(opp.value_amount),
  );
  const [capacityTotal, setCapacityTotal] = useState(
    opp.capacity_total == null ? "" : String(opp.capacity_total),
  );
  const [capacityUnit, setCapacityUnit] = useState<CapacityUnit>(
    opp.capacity_unit ?? "gpu",
  );
  const [expectedReady, setExpectedReady] = useState(
    opp.expected_ready_date ? opp.expected_ready_date.slice(0, 10) : "",
  );

  useEffect(() => {
    setTitle(opp.title);
    setStatus(opp.status);
    setValueAmount(opp.value_amount == null ? "" : String(opp.value_amount));
    setCapacityTotal(opp.capacity_total == null ? "" : String(opp.capacity_total));
    setCapacityUnit(opp.capacity_unit ?? "gpu");
    setExpectedReady(opp.expected_ready_date ? opp.expected_ready_date.slice(0, 10) : "");
  }, [opp]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => opportunities.update(opp.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunity", opp.id] });
      toast("success", "Saved");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t) {
      toast("error", "Title is required");
      return;
    }
    const body: Record<string, unknown> = { title: t, status };

    const va = valueAmount.trim();
    if (va === "") body.value_amount = null;
    else {
      const n = Number(va);
      if (Number.isNaN(n)) {
        toast("error", "Value must be a number");
        return;
      }
      body.value_amount = n;
    }

    const ct = capacityTotal.trim();
    if (ct === "") {
      body.capacity_total = null;
    } else {
      const n = Number(ct);
      if (Number.isNaN(n)) {
        toast("error", "Capacity must be a number");
        return;
      }
      body.capacity_total = n;
      body.capacity_unit = capacityUnit;
    }

    body.expected_ready_date = expectedReady ? expectedReady : null;

    save.mutate(body);
  };

  return (
    <Card title="Details">
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Title">
          <TextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={!editable}
          />
        </Field>
        <Field label="Status">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as OppStatus)}
            disabled={!editable}
            options={STATUS_OPTIONS}
          />
        </Field>
        <Field label="Value amount">
          <TextInput
            type="number"
            value={valueAmount}
            onChange={(e) => setValueAmount(e.target.value)}
            disabled={!editable}
            placeholder="—"
          />
        </Field>
        <Field label="Capacity total">
          <TextInput
            type="number"
            value={capacityTotal}
            onChange={(e) => setCapacityTotal(e.target.value)}
            disabled={!editable}
            placeholder="—"
          />
        </Field>
        <Field label="Capacity unit">
          <Select
            value={capacityUnit}
            onChange={(e) => setCapacityUnit(e.target.value as CapacityUnit)}
            disabled={!editable}
            options={CAPACITY_UNITS.map((u) => ({ value: u, label: u }))}
          />
        </Field>
        <Field label="Expected ready date">
          <TextInput
            type="date"
            value={expectedReady}
            onChange={(e) => setExpectedReady(e.target.value)}
            disabled={!editable}
          />
        </Field>
        {editable && (
          <div className="sm:col-span-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? <Spinner /> : "Save details"}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

/* ============================================== Type-specific details card */

function TypeDetailsCard({
  opp,
  funnelKey,
  editable,
}: {
  opp: OppDetail;
  funnelKey: FunnelKey | undefined;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const specs = funnelKey ? DETAIL_FIELDS[funnelKey] : [];

  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const f of specs) next[f.key] = detailStr(opp.details, f.key);
    setDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opp, funnelKey]);

  const save = useMutation({
    mutationFn: (details: Record<string, unknown>) =>
      opportunities.update(opp.id, { details }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunity", opp.id] });
      toast("success", "Saved");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  if (!funnelKey || specs.length === 0) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const details: Record<string, unknown> = {};
    for (const f of specs) {
      const raw = (draft[f.key] ?? "").trim();
      if (raw === "") {
        details[f.key] = null;
        continue;
      }
      if (f.type === "number") {
        const n = Number(raw);
        if (Number.isNaN(n)) {
          toast("error", `${f.label} must be a number`);
          return;
        }
        details[f.key] = n;
      } else {
        details[f.key] = raw;
      }
    }
    save.mutate(details);
  };

  const setField = (key: string, value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <Card title={`${funnelKey} details`}>
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {specs.map((f) => (
          <Field key={f.key} label={f.label}>
            {f.type === "select" ? (
              <Select
                value={draft[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
                disabled={!editable}
                options={[{ value: "", label: "—" }, ...(f.options ?? [])]}
              />
            ) : (
              <TextInput
                type={
                  f.type === "number" ? "number" : f.type === "date" ? "date" : "text"
                }
                value={draft[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
                disabled={!editable}
                placeholder="—"
              />
            )}
          </Field>
        ))}
        {editable && (
          <div className="sm:col-span-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? <Spinner /> : "Save details"}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

/* ===================================================== Metrics card */

function MetricRow({
  oppId,
  def,
  current,
  editable,
}: {
  oppId: string;
  def: MetricDefinition;
  current: MetricValue | undefined;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState(current?.value == null ? "" : String(current.value));

  useEffect(() => {
    setValue(current?.value == null ? "" : String(current.value));
  }, [current]);

  const save = useMutation({
    mutationFn: (v: unknown) => opportunities.setMetric(oppId, def.id, v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunity-metrics", oppId] });
      toast("success", `${def.label} saved`);
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const submit = () => {
    const raw = value.trim();
    let parsed: unknown = raw === "" ? null : raw;
    if (raw !== "" && def.value_type === "number") {
      const n = Number(raw);
      if (Number.isNaN(n)) {
        toast("error", `${def.label} must be a number`);
        return;
      }
      parsed = n;
    }
    save.mutate(parsed);
  };

  const enumOptions =
    def.value_type === "enum"
      ? (def.enum_options ?? []).map((o) => ({ value: String(o), label: String(o) }))
      : [];

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Field label={def.label}>
          {def.value_type === "enum" ? (
            <Select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={!editable}
              options={[{ value: "", label: "—" }, ...enumOptions]}
            />
          ) : (
            <TextInput
              type={
                def.value_type === "number"
                  ? "number"
                  : def.value_type === "date"
                    ? "date"
                    : "text"
              }
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={!editable}
              placeholder="—"
            />
          )}
        </Field>
      </div>
      {editable && (
        <Button size="sm" onClick={submit} disabled={save.isPending}>
          {save.isPending ? <Spinner /> : "Set"}
        </Button>
      )}
    </div>
  );
}

function MetricsCard({ opp, editable }: { opp: OppDetail; editable: boolean }) {
  const defsQuery = useQuery({
    queryKey: ["metric-definitions"],
    queryFn: () => funnels.metricDefinitions(),
  });

  const valuesQuery = useQuery({
    queryKey: ["opportunity-metrics", opp.id],
    queryFn: () => opportunities.metrics(opp.id),
  });

  const defs = useMemo(
    () =>
      (defsQuery.data ?? [])
        .filter((d) => !d.archived_at)
        .filter((d) => d.funnel_id == null || d.funnel_id === opp.funnel_id)
        .sort((a, b) => a.position - b.position),
    [defsQuery.data, opp.funnel_id],
  );

  const valueByDef = useMemo(() => {
    const m = new Map<string, MetricValue>();
    for (const v of valuesQuery.data ?? []) m.set(v.metric_definition_id, v);
    return m;
  }, [valuesQuery.data]);

  return (
    <Card title="Metrics">
      {defsQuery.isLoading || valuesQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner /> Loading metrics…
        </div>
      ) : defsQuery.isError ? (
        <EmptyState message={errMessage(defsQuery.error)} />
      ) : defs.length === 0 ? (
        <EmptyState message="No metrics defined" />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {defs.map((def) => (
            <MetricRow
              key={def.id}
              oppId={opp.id}
              def={def}
              current={valueByDef.get(def.id)}
              editable={editable}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

/* ========================================================= Notes card */

function NotesCard({ oppId, editable }: { oppId: string; editable: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [body, setBody] = useState("");

  const notesQuery = useQuery({
    queryKey: ["opportunity-notes", oppId],
    queryFn: () => opportunities.notes(oppId),
  });

  const add = useMutation({
    mutationFn: (text: string) => opportunities.addNote(oppId, text),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunity-notes", oppId] });
      setBody("");
      toast("success", "Note added");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = body.trim();
    if (!t) {
      toast("error", "Note cannot be empty");
      return;
    }
    add.mutate(t);
  };

  const notes = useMemo(
    () =>
      [...(notesQuery.data ?? [])].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [notesQuery.data],
  );

  return (
    <Card title="Notes">
      {editable && (
        <form onSubmit={submit} className="mb-4 space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add a note…"
            rows={3}
          />
          <Button type="submit" disabled={add.isPending}>
            {add.isPending ? <Spinner /> : "Add note"}
          </Button>
        </form>
      )}

      {notesQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner /> Loading notes…
        </div>
      ) : notesQuery.isError ? (
        <EmptyState message={errMessage(notesQuery.error)} />
      ) : notes.length === 0 ? (
        <EmptyState message="No notes yet" />
      ) : (
        <ul className="space-y-3">
          {notes.map((n: Note) => (
            <li key={n.id} className="rounded border border-slate-200 p-3">
              <div className="whitespace-pre-wrap text-sm text-slate-900">{n.body}</div>
              <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                <span>{n.author_user_id ?? "—"}</span>
                <span className="text-slate-300">·</span>
                <span>{formatDateTime(n.created_at)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ==================================================== Attachments card */

function AttachmentsCard({ oppId, editable }: { oppId: string; editable: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();

  const attQuery = useQuery({
    queryKey: ["opportunity-attachments", oppId],
    queryFn: () => opportunities.attachments(oppId),
  });

  const upload = useMutation({
    mutationFn: (file: File) => opportunities.uploadAttachment(oppId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunity-attachments", oppId] });
      toast("success", "File uploaded");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (attId: string) => opportunities.deleteAttachment(attId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunity-attachments", oppId] });
      toast("success", "Attachment deleted");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) upload.mutate(file);
    e.target.value = "";
  };

  return (
    <Card title="Attachments">
      {editable && (
        <div className="mb-4 flex items-center gap-3">
          <input
            type="file"
            onChange={onFile}
            disabled={upload.isPending}
            className="text-sm text-slate-600 file:mr-3 file:rounded file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-slate-700"
          />
          {upload.isPending && <Spinner />}
        </div>
      )}

      {attQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner /> Loading attachments…
        </div>
      ) : attQuery.isError ? (
        <EmptyState message={errMessage(attQuery.error)} />
      ) : (attQuery.data ?? []).length === 0 ? (
        <EmptyState message="No attachments" />
      ) : (
        <ul className="divide-y divide-slate-200">
          {(attQuery.data ?? []).map((att: Attachment) => (
            <li key={att.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0 flex-1">
                <a
                  href={apiUrl(`/attachments/${att.id}/download`)}
                  className="block truncate text-sm font-medium text-slate-900 hover:underline"
                >
                  {att.filename}
                </a>
                <div className="text-xs text-slate-400">
                  {formatBytes(att.size_bytes)}
                  <span className="text-slate-300"> · </span>
                  {formatDateTime(att.created_at)}
                </div>
              </div>
              {editable && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => remove.mutate(att.id)}
                  disabled={remove.isPending}
                >
                  Delete
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* =================================================== Stage history card */

function StageHistoryCard({
  transitions,
  stageName,
}: {
  transitions: Transition[];
  stageName: (id: string | null) => string;
}) {
  const ordered = useMemo(
    () =>
      [...transitions].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      ),
    [transitions],
  );

  return (
    <Card title="Stage history">
      {ordered.length === 0 ? (
        <EmptyState message="No transitions yet" />
      ) : (
        <ol className="space-y-3">
          {ordered.map((t, i) => {
            const prev = i > 0 ? ordered[i - 1] : null;
            const days = prev
              ? (new Date(t.created_at).getTime() - new Date(prev.created_at).getTime()) /
                86400000
              : null;
            return (
              <li key={t.id} className="flex items-start gap-3 text-sm">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-slate-300" />
                <div>
                  <div className="text-slate-900">
                    {stageName(t.from_stage_id)}{" "}
                    <span className="text-slate-400">→</span> {stageName(t.to_stage_id)}
                  </div>
                  <div className="text-xs text-slate-400">
                    {formatDateTime(t.created_at)}
                    {days != null && (
                      <>
                        <span className="text-slate-300"> · </span>
                        {days.toFixed(1)}d in previous stage
                      </>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}

/* =================================================== Capacity summary card */

function CapacityCard({ opp }: { opp: OppDetail }) {
  return (
    <Card title="Capacity & allocation">
      <dl className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <dt className="text-xs text-slate-500">Capacity total</dt>
          <dd className="mt-1 font-medium text-slate-900">
            {capacityLabel(opp.capacity_total, opp.capacity_unit)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Allocated</dt>
          <dd className="mt-1 font-medium text-slate-900">
            {opp.capacity_unit === "usd"
              ? formatCurrency(opp.allocated_amount)
              : formatNumber(opp.allocated_amount)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Available</dt>
          <dd className="mt-1 font-medium text-slate-900">
            {capacityLabel(opp.available_capacity, opp.capacity_unit)}
          </dd>
        </div>
      </dl>
    </Card>
  );
}

/* ========================================================== Page */

export default function OpportunityDetail() {
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const editable = canEdit(user?.role);

  const oppQuery = useQuery({
    queryKey: ["opportunity", id],
    queryFn: () => opportunities.get(id!),
    enabled: !!id,
  });

  const funnelsQuery = useQuery({
    queryKey: ["funnels"],
    queryFn: () => funnels.list(),
  });

  const opp = oppQuery.data;

  const funnel = useMemo(
    () => funnelsQuery.data?.find((f) => f.id === opp?.funnel_id),
    [funnelsQuery.data, opp?.funnel_id],
  );

  const stageById = useMemo(() => {
    const m = new Map<string, Stage>();
    for (const f of funnelsQuery.data ?? []) {
      for (const s of f.stages) m.set(s.id, s);
    }
    return m;
  }, [funnelsQuery.data]);

  const stageName = (sid: string | null): string => {
    if (!sid) return "—";
    return stageById.get(sid)?.name ?? sid;
  };

  if (!id) {
    return <EmptyState message="No opportunity selected" />;
  }

  if (oppQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Spinner /> Loading opportunity…
      </div>
    );
  }

  if (oppQuery.isError) {
    return <EmptyState message={errMessage(oppQuery.error)} />;
  }

  if (!opp) {
    return <EmptyState message="Opportunity not found" />;
  }

  const currentStage = stageById.get(opp.stage_id);

  return (
    <div className="space-y-4">
      <HeaderCard opp={opp} funnel={funnel} stage={currentStage} editable={editable} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CommonFieldsCard opp={opp} editable={editable} />
        <TypeDetailsCard opp={opp} funnelKey={funnel?.key} editable={editable} />
      </div>

      <CapacityCard opp={opp} />

      <MetricsCard opp={opp} editable={editable} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <NotesCard oppId={opp.id} editable={editable} />
        <AttachmentsCard oppId={opp.id} editable={editable} />
      </div>

      <StageHistoryCard transitions={opp.transitions} stageName={stageName} />
    </div>
  );
}
